/**
 * Renderer hook for the GUI phase strip (Crucible #416 Chunk C).
 *
 * Subscribes to `phase-progress:update` for the active gameId. Returns
 * the latest `PhaseProgressUpdate` (or `undefined` while the initial
 * subscribe is in flight). Internally:
 *
 *   1. Calls `phase-progress:subscribe` once per gameId. The IPC
 *      returns the current snapshot synchronously, so the strip can
 *      paint immediately.
 *   2. Listens on `phase-progress:update` for subsequent push events
 *      from main's `fs.watch`.
 *   3. Calls `phase-progress:unsubscribe` on cleanup so main can tear
 *      down its watcher.
 *
 * This hook NEVER reads `docs/build-progress.json`. The wire payload
 * contains only the fields produced by `derivePhase` + `parseErrorToProse`
 * — we never see raw bytes from the file.
 */

import { useEffect, useState } from 'react';
import type { PhaseProgressUpdate } from '@volley/crucible-ipc-types/ipc-channels';
import type { ProjectRef } from '@volley/crucible-ipc-types';
import { usePhaseEventsStore } from '../stores/phase-events';
import { useCrucibleClient } from '../transport/context';
import { isProjectWorkspaceRouteActive } from '../transport/project-route';

/** The latest transition `seq` in a derived update; undefined for non-derived. */
function latestPhaseSeq(update: PhaseProgressUpdate): number | undefined {
  if (update.kind !== 'derived') return undefined;
  return update.derived.transitions.at(-1)?.seq;
}

/**
 * The server-merged last-agent-activity stamp (absolute epoch-ms) carried by the
 * cloud `derived` payload — the ONLY monotonic whole-turn liveness signal on the
 * phase-progress wire (there is no `generatedAt`/`sourceRevision`). It ticks
 * ~every 2s while a turn is live, i.e. as within-phase `progress` / `buildOrder`
 * advance. `null`/absent (nothing recorded yet, or the desktop path) → undefined.
 */
function activityStamp(update: PhaseProgressUpdate): number | undefined {
  if (update.kind !== 'derived') return undefined;
  return typeof update.lastActivityAt === 'number' ? update.lastActivityAt : undefined;
}

/**
 * Whether a reconnect re-subscribe snapshot should replace the currently-applied
 * update (crucible#3011).
 *
 * The re-subscribe RESULT is the OLDEST frame of the freshly-restarted poll loop,
 * so it can lose a race against a live push that already landed on the still-
 * registered push handler. `transitions.seq` only bumps on a PHASE change, so a
 * seq-only guard let a stale re-subscribe wholesale-replace fresher WITHIN-phase
 * fields (`progress`, `buildOrder`, `lastActivityAt`) at an equal seq — rolling
 * the build-progress card + liveness backwards.
 *
 * Order by the phase-transition `seq` first (cross-phase), then fall back to
 * `lastActivityAt` (within-phase liveness) as the monotonic tiebreaker. Apply
 * only when strictly newer on the finest comparable axis; reject an equal-or-
 * older snapshot so it can never roll live progress back. A genuinely fresh-
 * after-gap update is never PERMANENTLY dropped — the now-live poll loop keeps
 * pushing, so the next push carries it. When neither side is a derived payload
 * with a stamp (a non-derived parse-error / no-progress-file / read-error state,
 * or the desktop path), take the fresh server read unless it would drop a
 * stamped live value for an unstamped one.
 */
function shouldApplyReconnectSnapshot(
  current: PhaseProgressUpdate | undefined,
  next: PhaseProgressUpdate,
): boolean {
  if (!current) return true;
  const curSeq = latestPhaseSeq(current);
  const nextSeq = latestPhaseSeq(next);
  if (curSeq !== undefined && nextSeq !== undefined) {
    if (nextSeq > curSeq) return true; // a phase advanced — unambiguously newer
    if (nextSeq < curSeq) return false; // the phase went backwards — stale
    // equal seq → same phase; fall through to the within-phase liveness tiebreaker
  }
  const curAct = activityStamp(current);
  const nextAct = activityStamp(next);
  if (curAct !== undefined && nextAct !== undefined) return nextAct > curAct;
  // Liveness stamp missing on one side. Never roll a stamped live value back to
  // an unstamped read; otherwise take the fresh server read (nothing live to
  // preserve, and the poll loop will re-push if it moves on).
  if (curAct !== undefined && nextAct === undefined) return false;
  return true;
}

export function usePhaseProgress(
  gameId: string | undefined,
  options: { enabled?: boolean; project?: ProjectRef } = {},
): PhaseProgressUpdate | undefined {
  const client = useCrucibleClient();
  const enabled = options.enabled ?? true;
  const projectPlacement = options.project?.placement;
  const projectGameId = options.project?.gameId;
  const [scopedUpdate, setScopedUpdate] = useState<
    { gameId: string; update: PhaseProgressUpdate } | undefined
  >(undefined);
  const setCurrentPhaseSeq = usePhaseEventsStore((s) => s.setCurrentPhaseSeq);
  const setGameId = usePhaseEventsStore((s) => s.setGameId);

  useEffect(() => {
    if (!gameId || !enabled) {
      setScopedUpdate(undefined);
      return;
    }
    // The state is keyed by gameId so the first render after a route
    // change cannot expose the previous game's update while this
    // post-commit clear and the next subscribe are still pending.
    setScopedUpdate(undefined);

    let cancelled = false;
    // Mirror the active gameId into the in-session events buffer so
    // events from a previous game don't bleed into the new game's
    // phase-scoped detail panel.
    setGameId(gameId);

    const unsub = client.subscribe('phase-progress:update', (payload) => {
      if (cancelled) return;
      // Filter cross-game pushes — the renderer can have multiple
      // games' watchers active in main if a power user is bouncing
      // between Workspace tabs (we don't, today, but be defensive).
      if (payload.gameId !== gameId) return;
      setScopedUpdate({ gameId, update: payload });
      if (payload.kind === 'derived') {
        const last = payload.derived.transitions.at(-1);
        if (last) setCurrentPhaseSeq(last.seq);
      }
    });

    void client
      .invoke('phase-progress:subscribe', { gameId })
      .then((snapshot) => {
        if (cancelled) return;
        if (snapshot.gameId !== gameId) return;
        setScopedUpdate({ gameId, update: snapshot });
        if (snapshot.kind === 'derived') {
          const last = snapshot.derived.transitions.at(-1);
          if (last) setCurrentPhaseSeq(last.seq);
        }
      })
      .catch(() => {
        // Subscribe failures degrade safely: the strip does not render and
        // automatic Preview waits rather than guessing. Manual Preview stays
        // available and main's authoritative preflight still decides it.
        // Main-side errors are already surfaced through `editor:error` and
        // the diagnostic blob.
      });

    // crucible#3011: a websocket reconnect mints a NEW cloud session and the
    // server tears down this game's phase-progress poll loop (keyed by the old
    // sessionId). The client-side push subscription above survives the
    // reconnect, but nothing restarts the server loop — so the build-progress
    // card would freeze on a stale snapshot until the Workspace remounts.
    // Re-issue `phase-progress:subscribe` on the client's reconnect signal
    // (Electron's is an inert no-op); only the server loop needs restarting.
    const applyReconnectSnapshot = (snapshot: PhaseProgressUpdate) => {
      if (cancelled) return;
      if (snapshot.gameId !== gameId) return;
      // Compare against the LIVE applied value inside the updater: a push may have
      // landed on the still-registered handler while this re-subscribe was in
      // flight. `shouldApplyReconnectSnapshot` rejects a snapshot that is not
      // strictly newer (by phase seq, then `lastActivityAt`), so a stale
      // re-subscribe can't wholesale-replace fresher within-phase progress.
      setScopedUpdate((prev) => {
        const current = prev && prev.gameId === gameId ? prev.update : undefined;
        if (!shouldApplyReconnectSnapshot(current, snapshot)) return prev;
        return { gameId, update: snapshot };
      });
      if (snapshot.kind === 'derived') {
        const last = snapshot.derived.transitions.at(-1);
        // `setCurrentPhaseSeq` is not monotonic — only advance it, never let a
        // stale re-subscribe regress the phase-scoped detail selection. (A stale
        // equal-seq re-subscribe bumps to the same value → a no-op.)
        if (last && last.seq >= usePhaseEventsStore.getState().currentPhaseSeq) {
          setCurrentPhaseSeq(last.seq);
        }
      }
    };
    const unsubscribeReconnect = client.subscribeReconnect?.(() => {
      if (cancelled) return;
      void client
        .invoke('phase-progress:subscribe', { gameId })
        .then(applyReconnectSnapshot)
        .catch(() => {
          // Reconnect re-subscribe failures degrade like the initial subscribe:
          // the strip keeps its last snapshot until a push or the next
          // reconnect refreshes it.
        });
    });

    return () => {
      cancelled = true;
      unsub();
      unsubscribeReconnect?.();
      const project =
        projectPlacement && projectGameId
          ? { placement: projectPlacement, gameId: projectGameId }
          : undefined;
      if (!project || isProjectWorkspaceRouteActive(project)) {
        void client.invoke('phase-progress:unsubscribe', { gameId }).catch(() => {
          // Best effort. Route-leave cleanup is main-owned because navigation
          // authority is revoked before passive React cleanup runs.
        });
      }
    };
  }, [client, gameId, enabled, projectGameId, projectPlacement, setCurrentPhaseSeq, setGameId]);

  if (!gameId || !enabled || scopedUpdate?.gameId !== gameId) return undefined;
  return scopedUpdate.update;
}
